import { MemberPurchaseService } from 'src/app/services/member-purchase.service';
import { MemberReportService } from 'src/app/services/member-report.service';
import { type MemberPurchase, attachMemberPurchases } from 'src/app/utils/member-purchase';
import { type MemberRow, purchaseToRow, reportToRow } from 'src/app/utils/member-row';
import { type MemberReport, attachMemberReports, lineForProductRow } from 'src/app/utils/member-report';
import { POPULAR_BOUGHT_PAST_MONTH } from 'src/app/utils/bought-past-month';
import { isHiddenStore } from 'src/app/utils/store-visibility';
import { loadAreaUsage, noteAreaUsage, selectedAreaLabels, withFavoriteAreas } from 'src/app/utils/favorite-areas';
import { Component, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, UntypedFormGroup } from '@angular/forms';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { EdionProduct, EdionStock } from 'src/app/models/edion-product';
import { MessageService } from 'src/app/services/message.service';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import { EdionProductsGQL } from 'src/gql/generated';
import { LoggingGQL } from '../../../gql/generated';
import { calcFbaProfit } from '../../utils/fba-profit';
import { pickAmazonPrice } from '../../utils/amazon-price';
import { sortByField } from '../../utils/sort-by-field';
import { SpreadsheetStockBadgeComponent } from '../spreadsheet-stock-badge/spreadsheet-stock-badge.component';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { PanelModule } from 'primeng/panel';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { KeepaGraphComponent } from '../parts/keepa-graph/keepa-graph.component';
import { MemberRowTableComponent } from '../parts/member-row-table/member-row-table.component';
import { StorePresetDialogComponent } from '../store-preset-dialog/store-preset-dialog.component';
import { StorePreset } from '../../services/store-preset.service';
import { isSoldOutByPurchase } from 'src/app/utils/purchase-state';

/** 商品共有の実績のうち、この仕入店舗のものを在庫一覧に出す */
const MEMBER_PURCHASE_STORE = 'エディオン';
/** みんなの報告（LINE）のうち、店名にこの言葉を含むものを在庫一覧に出す */
const MEMBER_REPORT_CHAIN = 'エディオン';

@Component({
  selector: 'app-edion',
  templateUrl: './edion.component.html',
  styleUrl: './edion.component.sass',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    MultiSelectModule,
    SelectModule,
    CheckboxModule,
    PanelModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    SpreadsheetStockBadgeComponent,
    KeepaGraphComponent,
    MemberRowTableComponent,
    DatePipe,
    IconFieldModule,
    InputIconModule,
    StorePresetDialogComponent,
  ],
})
export class EdionComponent {
  searchText: string;
  form: UntypedFormGroup;
  isLoading: boolean;

  products: Array<EdionProduct> = [];
  allProducts: Array<EdionProduct> = [];
  /** 表の上の「並び替え」。null は取得した順（標準） */
  sortOptions = [
    { label: '標準（登録順）', value: null },
    { label: '利益額が高い順', value: 'profitPrice-desc' },
    { label: '利益額が低い順', value: 'profitPrice-asc' },
    { label: '利益率が高い順', value: 'profitRate-desc' },
    { label: '利益率が低い順', value: 'profitRate-asc' },
    { label: '先月販売数が多い順', value: 'boughtPastMonth-desc' },
    { label: '先月販売数が少ない順', value: 'boughtPastMonth-asc' },
    { label: 'POS価格が高い順', value: 'posPrice-desc' },
    { label: 'POS価格が安い順', value: 'posPrice-asc' },
  ];
  selectedSort: string | null = null;
  /** 検索パネル「先月販売数が50以上の商品だけ表示する」。最初はオフ＝全部出す（2026-10-14 ユーザー指定） */
  onlyPopular = false;
  /** 検索パネル「利益率・利益額がマイナスの商品を非表示にする」（2026-10-12 ユーザー指定）。利益が計算できない商品は残す */
  hideNegativeProfit = false;
  /** 検索パネル「みんなの仕入れ実績・LINE報告がある商品だけ表示する」（2026-10-14 ユーザー指定） */
  onlyWithMember = false;
  /** 商品共有の実績のうち、在庫一覧のどの商品にも付かなかったもの（改廃リストに無い商品）。画面の下に出す */
  otherPurchases: MemberPurchase[] = [];
  /** 画面の下の一覧（在庫一覧と同じ形）に出す行 */
  otherPurchaseRows: MemberRow[] = [];
  /** みんなの報告（LINE）のうち、在庫一覧のどの商品にも付かなかったもの。画面の下に出す */
  otherReports: MemberReport[] = [];
  otherReportRows: MemberRow[] = [];
  /** 取得した順（「標準」に戻すときに使う） */
  private loadedOrder: EdionProduct[] = [];
  showPurchased = false;
  /** 検索窓のキーワード。在庫一覧（p-table の filterGlobal）と、下の一覧（みんなの仕入れ実績・LINE 報告）の両方に効く */
  keyword = '';
  showDisplay = false;
  showFound = false;
  filterOneStock = false;
  isTestAccount = false;

  canRegisterNewItems = true;

  finishedBuildStores = false;
  selectedArea = [];
  groupedArea = [];
  allStores = [];
  selectedStores = [];
  groupedStores = [];

  queueItems = [];

  registrationDateOptions = [
    { name: '7日以内', value: 7 },
    { name: '14日以内', value: 14 },
    { name: '30日以内', value: 30 },
    { name: '60日以内', value: 60 },
    { name: '全て', value: 0 },
  ];
  filterRegistrationDate = { name: '30日以内', value: 30 };

  splitStr = '::';
  selectedAreaKey = 'savedEdionSelectedAreaKey';
  selectedStoreKey = 'savedEdionSelectedStoreKey';

  constructor(
    private elementRef: ElementRef,
    private memberPurchases: MemberPurchaseService,
    private memberReports: MemberReportService,
    private message: MessageService,
    private subtitleService: SubtitleService,
    private routingService: RoutingService,
    private userService: UserService,
    private getGql: EdionProductsGQL,
    private loggingGql: LoggingGQL,
  ) {
    this.isLoading = false;
    this.routingService.loginRequired();
    this.userService.spreadsheetRequired();
    this.isTestAccount = this.userService.getEmail() === 'test@rakujan.com';
    this.subtitleService.setSubTitle('エディオン');
  }

  async ngOnInit() {
    this.selectedArea = JSON.parse(localStorage.getItem(this.selectedAreaKey)) || [];
    this.selectedStores = JSON.parse(localStorage.getItem(this.selectedStoreKey)) || [];
    await this.loadStocks();
  }

  async loadStocks() {
    var regDate =
      this.filterRegistrationDate?.value && this.filterRegistrationDate?.value > 0
        ? moment().subtract(this.filterRegistrationDate.value, 'days').format('YYYY/MM/DD')
        : null;
    const resp = await firstValueFrom(
      this.getGql.fetch({
        variables: {
          registrationDate: regDate,
        },
      }),
    );
    this.products = resp.data.edionProducts.map((p) => {
      var hasAnyStock = false;
      const stocks = p.stocks.map((s) => {
        hasAnyStock =
          hasAnyStock ||
          !isSoldOutByPurchase({
            isPurchased: s.operation?.isPurchased || false,
            purchasedQuantity: s.operation?.purchasedQuantity ?? null,
            stockNum: s.stockNum,
          });
        return {
          type: 'edion',
          id: s.id,
          createdAt: s.createdAt,
          isPurchased: s.operation?.isPurchased || false,
          purchasedUserName: s.operation?.purchasedUser?.name || '',
          purchasedAt: s.operation?.purchasedAt || '',
          purchasedQuantity: s.operation?.purchasedQuantity ?? null,
          isDisplay: s.operation?.isDisplay || false,
          displayUserName: s.operation?.displayUser?.name || '',
          displaySetAt: s.operation?.displaySetAt || '',
          isNotFound: s.operation?.isNotFound || false,
          notFoundUserName: s.operation?.notFoundUser?.name || '',
          notFoundAt: s.operation?.notFoundAt || '',
          isRestricted: s.operation?.isRestricted || false,
          restrictedUserName: s.operation?.restrictedUser?.name || '',
          restrictedAt: s.operation?.restrictedAt || '',
          isSkipped: s.operation?.isSkipped || false,
          skippedUserName: s.operation?.skippedUser?.name || '',
          skippedAt: s.operation?.skippedAt || '',
          isStaffNoStock: s.operation?.isStaffNoStock || false,
          staffNoStockUserName: s.operation?.staffNoStockUser?.name || '',
          staffNoStockAt: s.operation?.staffNoStockAt || '',
          memo: s.operation?.memo,
          memoUserName: s.operation?.memoUser?.name || '',
          memoAddedAt: s.operation?.memoAddedAt || '',
          stockNum: s.stockNum,
          storeId: s.store.id,
          storeCode: s.store.code,
          storeName: s.store.name,
          areaId: s.store.area.id,
          areaName: s.store.area.name,
        } as EdionStock;
      });

      const sp = p.product;
      const mainAp = sp.amazonProducts.find((ap) => ap.isMain);
      const ap = mainAp ?? (sp.amazonProducts.length > 0 ? p.product.amazonProducts[0] : null);
      const amazon = pickAmazonPrice(ap);
      return {
        id: p.id,
        jan: sp.jan,
        asin: ap?.asin || '',
        posPrice: p.posPrice,
        discountPrice: p.discountPrice,
        amazonPrice: amazon.price,
        amazonPriceSource: amazon.source,
        lastKnownPriceAt: amazon.at,
        imageUrl: ap?.imageUrl || '',
        boughtPastMonth: ap?.keepaProduct?.boughtPastMonth,
        ...calcFbaProfit({
          amazonPrice: amazon.price,
          // 値下げ価格が入っていればその値段で仕入れる。0 や空は「値下げなし」なので POS 価格を使う
          cost: p.discountPrice && p.discountPrice > 0 ? p.discountPrice : p.posPrice,
          salesCommission: ap?.salesCommission,
          categoryFee: ap?.categoryFee,
          fbaShippingFee: ap?.fbaShippingFee,
        }),
        totalStocks: p.totalStocks,
        name: ap?.name || '',
        disposalRank: p.disposalRank,
        registrationDate: p.registrationDate,
        hasAnyStock: hasAnyStock,
        stocks: stocks,
        filteredStocks: this.doFilters(stocks),
      };
    });

    await this.loadMemberPurchases();
    this.allProducts = [...this.products];
    this.loadedOrder = [...this.products];
    this.applySort();

    var area = {
      label: '都道府県',
      value: 'area',
      items: [],
    };
    this.groupedStores = [];

    resp.data.storeAreas.forEach((sa) => {
      area.items.push({
        label: sa.name,
        value: sa.id,
      });

      var store = {
        label: sa.name,
        value: sa.id,
        items: [],
      };
      sa.edionStores.forEach((s) => {
        if (isHiddenStore(s.name)) return; // 配工センターなど在庫を入れない拠点は選択肢に出さない
        store.items.push({
          label: s.name,
          value: s.id,
        });
      });
      this.groupedStores.push(store);
    });

    this.allStores = [...this.groupedStores];
    // よく使う都道府県（このブラウザで選んだ回数の多い順に 5 件）を先頭に出す（2026-10-15 ユーザー指定）
    this.groupedArea = withFavoriteAreas([area], loadAreaUsage());

    if (this.selectedArea.length > 0) {
      this.doChangeArea(null);
    }
    if (this.selectedStores.length > 0) {
      this.doChangeStores(null);
    }
    this.logging();
  }

  async doChangeRegistrationDate($e) {
    await this.loadStocks();
  }

  async reloadStocks($e) {
    await this.loadStocks();
  }

  clearArea() {
    this.selectedArea = [];
    this.doChangeArea(null);
  }

  clearStores() {
    this.selectedStores = [];
    this.doChangeStores(null);
  }

  onPresetLoaded(preset: StorePreset) {
    this.selectedArea = preset.selectedArea || [];
    this.selectedStores = preset.selectedStores || [];
    this.rememberAreaUsage();
    this.doChangeArea(null);
    this.doChangeStores(null);
  }

  applySort() {
    const order = (list: EdionProduct[]): EdionProduct[] => {
      if (!this.selectedSort) {
        const shown = new Set(list);
        return this.loadedOrder.filter((p) => shown.has(p));
      }
      const [field, dir] = this.selectedSort.split('-');
      return sortByField(list, field as keyof EdionProduct, dir === 'desc');
    };
    this.allProducts = order(this.allProducts);
    this.products = order(this.products);
  }

  /** allProducts から表示する商品を作り直す（店舗の絞り込み → 先月販売数の絞り込み） */
  rebuildProducts() {
    this.products = [];
    this.allProducts.forEach((p) => {
      p.filteredStocks = this.doFilters(p.stocks);
      if (p.filteredStocks.length > 0 && this.passesProductFilter(p)) {
        this.products.push(p);
      }
    });
  }

  applyProductFilter() {
    this.rebuildProducts();
  }

  /** 商品共有（せどりすと）の直近の仕入れ実績を各商品に付ける。取れなくても一覧は出す */
  private async loadMemberPurchases() {
    try {
      const purchases = await this.memberPurchases.loadRecent(MEMBER_PURCHASE_STORE);
      this.otherPurchases = attachMemberPurchases(this.products, purchases);
      this.otherPurchaseRows = this.otherPurchases.map(purchaseToRow);
    } catch (e) {
      console.error('みんなの仕入れ実績が取れませんでした', e);
      this.otherPurchases = [];
      this.otherPurchaseRows = this.otherPurchases.map(purchaseToRow);
    }
    try {
      const reports = await this.memberReports.loadRecent(MEMBER_REPORT_CHAIN);
      this.otherReports = attachMemberReports(this.products, reports);
      // 報告の行は「LINE 誰が いつ」だけ（仕入値が商品の仕入値と違う時だけ添える）
      for (const p of this.products) {
        p.memberReports = (p.memberReports ?? []).map((r) =>
          lineForProductRow(r, { cost: p.discountPrice && p.discountPrice > 0 ? p.discountPrice : p.posPrice }),
        );
      }
      this.otherReportRows = this.otherReports.map(reportToRow);
    } catch (e) {
      console.error('みんなの報告（LINE）が取れませんでした', e);
      this.otherReports = [];
      this.otherReportRows = this.otherReports.map(reportToRow);
    }
  }

  /** 下の一覧の削除ボタンで LINE 報告が消えた時: 一覧と件数からもその報告を外す */
  onReportDeleted(id: number): void {
    this.otherReports = this.otherReports.filter((m) => m.id !== id);
    this.otherReportRows = this.otherReports.map(reportToRow);
  }

  private passesProductFilter(p: EdionProduct): boolean {
    if (this.onlyWithMember && !(p.memberPurchases?.length || p.memberReports?.length)) {
      return false;
    }
    if (this.hideNegativeProfit && p.profitPrice !== null && p.profitPrice !== undefined && p.profitPrice < 0) {
      return false;
    }
    if (this.onlyPopular && (p.boughtPastMonth ?? -1) < POPULAR_BOUGHT_PAST_MONTH) {
      return false; // 先月販売数が 50 未満・未取得の商品は出さない
    }
    return true;
  }

  /** 選んだ都道府県の回数を覚えて、「よく使う」グループを並べ直す */
  rememberAreaUsage() {
    const usage = noteAreaUsage(selectedAreaLabels(this.groupedArea, this.selectedArea));
    this.groupedArea = withFavoriteAreas(this.groupedArea, usage);
  }

  doChangeArea($e) {
    localStorage.setItem(this.selectedAreaKey, JSON.stringify(this.selectedArea));
    if ($e) this.rememberAreaUsage();
    this.rebuildProducts();
    this.groupedStores = this.allStores.filter((s) => {
      return this.selectedArea.includes(s.value);
    });
    this.logging();
  }

  doChangeStores($e) {
    localStorage.setItem(this.selectedStoreKey, JSON.stringify(this.selectedStores));
    this.rebuildProducts();
    this.logging();
  }

  doFilters(stocks: EdionStock[]) {
    stocks = this.doFilterStocks(stocks);
    stocks = this.doFilterArea(stocks);
    stocks = this.doFilterStores(stocks);
    return stocks;
  }

  private doFilterStocks(stocks: EdionStock[]) {
    if (stocks.length === 0) {
      return stocks;
    }
    const needsToFilter = this.filterOneStock || !this.showPurchased || !this.showDisplay || this.showFound;
    if (needsToFilter) {
      return stocks.filter((s) => {
        if (this.filterOneStock) {
          if (s.stockNum <= 1) {
            return false;
          }
        }
        if (!this.showPurchased) {
          // 購入で売り切れた店だけ隠す。在庫が残る店（5 個中 3 個購入など）は出す
          if (isSoldOutByPurchase(s)) {
            return false;
          }
        }
        if (!this.showDisplay) {
          if (s.isDisplay) {
            return false;
          }
        }
        if (this.showFound) {
          // 店頭無・店員無（どちらも「その店に無かった」）を隠す
          if (s.isNotFound || s.isStaffNoStock) {
            return false;
          }
        }
        return true;
      });
    }
    return stocks;
  }

  private doFilterArea(stocks: EdionStock[]) {
    if (this.selectedArea.length === 0 || stocks.length === 0) {
      return stocks;
    }
    // filter pref and store nmaes
    return stocks.filter((s) => {
      return this.selectedArea.includes(s.areaId);
    });
  }

  private doFilterStores(stocks: EdionStock[]) {
    if (this.selectedStores.length === 0 || stocks.length === 0) {
      return stocks;
    }
    // filter pref and store nmaes
    return stocks.filter((s) => {
      return this.selectedStores.includes(s.storeId);
    });
  }

  getRowStyle(product: EdionProduct) {
    if (!product.hasAnyStock) {
      if (this.showPurchased) {
        return 'unpublic';
      }
      return 'hide-table-row';
    }
    const isAllPurchased = product.filteredStocks.filter((s) => s.isPurchased).length === 0;
    if (isAllPurchased) {
      if (this.showPurchased) {
        return 'unpublic';
      }
      return 'hide-table-row';
    }

    return '';
  }

  downloadCsv(table: any) {
    const products: EdionProduct[] = table.filteredValue ?? this.products;
    const values = products.filter((p) => p.filteredStocks.length > 0);
    const csv = values
      .map((p) => {
        const stores = p.filteredStocks.map((s) => s.storeName + ':' + s.stockNum).join('/');
        const date = moment(p.registrationDate).format('MMDD');
        const totalStock = p.totalStocks != null ? p.totalStocks : '-';
        const boughtPastMonth = p.boughtPastMonth != null ? p.boughtPastMonth : '-';
        const memo = `${date}_総在${totalStock}_先販${boughtPastMonth}_${stores}`;
        return p.jan + ',' + p.posPrice + ',1,,"' + (p.discountPrice ?? '') + '_' + (memo ?? '') + '"';
      })
      .join('\n');
    this.outputCsv(csv, 'edion.csv');
  }

  private outputCsv(csv: string, filename: string) {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const elem = this.elementRef.nativeElement;
    const link = elem.querySelector('#csv-donwload') as HTMLAnchorElement;
    link.href = url;
    link.download = filename;
    link.click();
  }

  private async logging() {
    this.userService.me;
    const areaNames = this.selectedArea.map((id) => {
      const area = this.allStores.find((a) => a.value === id);
      return area ? area.label : id;
    });
    const storeNames = this.selectedStores.map((id) => {
      for (const area of this.allStores) {
        const s = area.items.find((x) => x.value === id);
        if (s) return s.label;
      }
      return id;
    });
    await firstValueFrom(
      this.loggingGql.fetch({
        variables: {
          name: 'edion',
          log: JSON.stringify([this.userService.getName(), areaNames, storeNames]),
        },
      }),
    );
  }
}
